import { useState, useReducer, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { sortBy, reverse } from "lodash";
import { SelectBox, RadioGroup } from "devextreme-react";
import { NumberBox } from "devextreme-react/number-box";
import { getTheme } from "../highChartTheme";

import * as actions from "../../../actions/ophAttainment";
import ophAttainmentReducer from "../../../reducers/ophAttainment";
import { Switch } from "devextreme-react";
import { postUserLog } from "../../../actions/common";

const initState = { ophMissReasons: [] };

const ParetoChart = (props) => {
  const [animate, setAnimate] = useState("");
  const [missReason, setMissReason] = useState([]);
  const [colorCode, setColorCode] = useState([]);
  const [graphSwitch, setGraphSwitch] = useState(true);
  const [reasonLevel, setReasonLevel] = useState(0);
  const [isCascaded, setIsCascaded] = useState(true);
  const [numberOfReasons, setNumberOfReasons] = useState(15);
  const isLightMode = useSelector((state) => state.theme.lightMode);
  const { refreshData } = useSelector((state) => state.ophDataRefresh);

  const [state, dispatch] = useReducer(ophAttainmentReducer, initState);

  const { tierId, localTime, siteId, navigationOn } = props;

  const { ophMissReasons } = state;

  const isInitialMount = useRef(true);

  useEffect(() => {
    actions.getOPHMissReasonGraph(dispatch, {
      tierId,
      startDate: moment().subtract("60", "days").format("YYYY-MM-DD"),
      endDate: moment(localTime).format("YYYY-MM-DD"),
      reasonLevel,
      rowCount: numberOfReasons,
      isCascaded,
    });
    setTimeout(() => {
      setAnimate("animate");
    }, 250);
  }, [refreshData, reasonLevel, numberOfReasons, isCascaded]);

  useEffect(() => {
    if (ophMissReasons.length) {
      const missReasonData = ophMissReasons?.map((x) => {
        return [x.reasonCode];
      });
      const missReasonColorData = ophMissReasons?.map((x) => {
        return [x.reasonColorCode];
      });
      setMissReason(missReasonData?.map(([reasonCode]) => reasonCode));
      setColorCode(missReasonColorData?.map(([colorCode]) => colorCode));
    }
  }, [ophMissReasons]);

  useEffect(() => {
    if (!isInitialMount.current) {
      graphSwitch
        ? postUserLog({
            event: "Pareto",
            data: {
              action: "Freq. of Occur Toggle Clicked",
              module: "Digital Tier",
              section: navigationOn,
              siteUId: siteId,
              widget: "Delivery Metric: OPH Attainment",
              tierId: tierId,
            },
          })
        : postUserLog({
            event: "Pareto",
            data: {
              action: "Missed Quantity Toggle Clicked",
              module: "Digital Tier",
              section: navigationOn,
              siteUId: siteId,
              widget: "Delivery Metric: OPH Attainment",
              tierId: tierId,
            },
          });
    }
    isInitialMount.current = false;
  }, [graphSwitch]);

  const options = {
    chart: {
      type: "column",
    },
    tooltip: {
      shared: true,
    },
    series: [
      {
        showInLegend: false,
        name: "",
        type: "column",
        colorByPoint: true,
        colors: colorCode,
        data: reverse(
          sortBy(ophMissReasons, [
            graphSwitch ? "reasonCodeCount" : "missedQty",
          ])
        )?.map((x) => {
          return [
            x.reasonCode,
            parseInt(graphSwitch ? x.reasonCodeCount : x.missedQty),
          ];
        }),
      },
    ],
  };

  return (
    <>
      <div
        style={{
          float: "right",
          margin: "10px 0px",
        }}
        className="rail-switch"
      >
        <Switch
          value={graphSwitch}
          switchedOnText="Freq Of Occur."
          switchedOffText="Missed Qty"
          width="110px"
          onValueChanged={() => {
            setGraphSwitch(!graphSwitch);
          }}
        />
      </div>
      <div className="site-view tier-wrap admin-console-wrapper">
        <div
          className={"site-list " + animate}
          style={{
            backgroundColor: isLightMode ? "#d3d3d3" : "#2f3030",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "end",
             }}
          >
            <div
              style={{
                marginRight: "10px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                Number of Reasons
              </span>
              <NumberBox
                min={1}
                width={168}
                step={5}
                showSpinButtons={true}
                value={numberOfReasons}
                onValueChanged={(e) => {
                  {
                    if (e.value > 0) {
                      setNumberOfReasons(e.value);
                    }
                  }
                }}
              />
            </div>
            <div>
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                Select Level
              </span>
              <SelectBox
                dataSource={[
                  {
                    label: "Level 0",
                    value: 0,
                  },
                  {
                    label: "Level 1",
                    value: 1,
                  },
                  {
                    label: "Level 2",
                    value: 2,
                  },
                ]}
                displayExpr="label"
                valueExpr="value"
                wrapItemText={true}
                onValueChanged={(e) => {
                  setReasonLevel(e.value);
                }}
                width={200}
                value={reasonLevel}
              />
            </div>
            <div
              style={{
                marginLeft: "10px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                Level Drilldown
              </span>
              <RadioGroup
                hint="Level Drilldown"
                items={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
                valueExpr="value"
                displayExpr="label"
                onValueChanged={(e) => {
                  setIsCascaded(e.value);
                }}
                layout="horizontal"
                value={isCascaded}
                style={{
                  marginTop: "10px",
                }}
              />
            </div>
          </div>
          <HighchartsReact
            highcharts={Highcharts}
            options={{
              ...options,
              ...getTheme(
                `OPH ${
                  graphSwitch ? "Miss Reasons" : "Missed Quantity"
                } - Pareto Chart`,
                isLightMode,
                missReason
              ),
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ParetoChart;
